import React, { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { ApolloClient } from '@apollo/client';
import getDefaultClient from 'src/apollo/client';
import { LTA_LOGOUT } from 'src/components/lta-memberships/lta-memberships-queries';
import Spinner from 'src/components/spinner/spinner';
import { getClientConfig, getEnvConfig, getRootProviderId } from 'src/config/config';
import sentrySetup from 'src/config/sentry-setup';
import { logoutQuery, logoutQueryVariables } from 'src/graphql-types/lta-registration/graphql';
import { retrieveLTAToken } from 'src/utils/storage/local-storage';
import { getBaseUrl } from 'src/utils/url-management';

import authService from './AuthService';
import { AuthEvent, AuthState, isBrowser, LoginError, User, UserVenue } from './types/auth.types';

interface AuthContextState {
  token: string | null;
  user: User | null;
  userVenues: UserVenue[] | null;
  isAuthenticated: boolean | null;
  authError: LoginError | null;
}
interface AuthContextProps extends AuthContextState {
  login: (redirectPath?: string) => void;
  logOut: (client?: ApolloClient<any>) => Promise<void>;
  resetSession: (client?: ApolloClient<any>, redirectUrl?: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextProps | undefined>(undefined);

type AuthProviderProps = {
  children: ReactNode;
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [{ token, user, userVenues, isAuthenticated, authError }, setAuthContextState] = useState<AuthContextState>({
    token: null,
    user: null,
    userVenues: null,
    isAuthenticated: null,
    authError: null,
  });
  const isBooting = isAuthenticated === null;

  const resetSession = useCallback(async () => {
    await authService.resetAuth();
  }, []);

  const logOut = useCallback(
    async (client?: ApolloClient<any>) => {
      if (!isBrowser) return;

      const isLta = getClientConfig().useLtaLogout;
      const logOutUrl = isLta ? getLtaSignOutUrl() : getAuthUrls().signOut;

      if (isLta) {
        await ltaLogout(client);
      }

      await resetSession();
      window.location.assign(logOutUrl);
    },
    [resetSession],
  );

  const login = useCallback((redirectPath: string = '/') => {
    if (!isBrowser) return;

    window.location.assign(getAuthUrls(redirectPath).signIn);
  }, []);

  useEffect(() => {
    const setAuthContext: EventListenerOrEventListenerObject = function (event) {
      const {
        authState,
        token = null,
        user = null,
        userVenues = null,
        authError = null,
      } = (
        event as CustomEventInit<{
          authState: AuthState;
          token: string | null;
          user: User | null;
          userVenues: UserVenue[] | null;
          authError: LoginError;
        }>
      ).detail ?? {};

      setAuthContextState({
        token,
        user,
        userVenues,
        authError,
        isAuthenticated: authState === AuthState.AUTHENTICATED,
      });
    };

    const authStateChangeEvent = authService.subscribe(AuthEvent.STATE_CHANGE, setAuthContext);

    authService.start(getDefaultClient());

    return () => {
      authStateChangeEvent.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (isBrowser) {
      process.env.NODE_ENV !== 'development' && sentrySetup();
    }
  }, []);

  const contextValue = useMemo(
    () => ({ user, userVenues, login, logOut, resetSession, token, isAuthenticated, authError }),
    [user, userVenues, login, logOut, resetSession, token, isAuthenticated, authError],
  );

  return (
    <AuthContext.Provider value={contextValue}>{isBooting ? <Spinner fullScreen /> : children}</AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);

  if (!isBrowser) return {} as AuthContextProps;

  if (!context) throw new Error('useAuth must be used within an AuthProvider');

  return context;
};

export const resetUserSession = async (client?: ApolloClient<any>) => {
  if (isBrowser) {
    await client?.clearStore();
    await authService.resetAuth();
  }
};

export const authPing = async () => {
  if (isBrowser) {
    await authService.authPing();
  }
};

const getLtaSignOutUrl = () => {
  const returnUrl = `${getBaseUrl()}/login`;

  return `${getEnvConfig().CLUBSPARK_CLASSIC_URL}/Account/SignOut?returnUrl=${encodeURIComponent(returnUrl)}&source=ra`;
};

const getAuthUrls = (redirectOnLoginPath: string = '/') => {
  const origin = isBrowser ? window.location.origin : '';
  const accountUrl = `${getEnvConfig().CLUBSPARK_CLASSIC_URL}/Account`;
  const signInReturnUrl = encodeURIComponent(
    `${origin}/login?auto=1&redirectOnLogin=${encodeURIComponent(redirectOnLoginPath)}`,
  );
  const signOutReturnUrl = encodeURIComponent(`${origin}/login`);
  const sourceParam = getClientConfig().useTenantSso ? `&sourceID=${getRootProviderId()}` : '';
  return {
    signIn: `${accountUrl}/SignIn?returnUrl=${signInReturnUrl}${sourceParam}`,
    signOut: `${accountUrl}/SignOut?returnUrl=${signOutReturnUrl}${sourceParam}`,
  };
};

const ltaLogout = async (client?: ApolloClient<any>) => {
  try {
    await client?.query<logoutQuery, logoutQueryVariables>({
      query: LTA_LOGOUT,
      variables: { authCode: retrieveLTAToken() || '' },
    });
  } catch (err) {
    console.error('Error logging out from LTA', err);
  }
};
